import eventbus from 'tiny-emitter/instance';
import invokeMap from 'lodash/invokeMap';
import sortBy from 'lodash/sortBy';
import map from 'lodash/map';
import { TYPE, SEAT, RENDERER_MODE, SEATSTATUS, EVENTSTATUS } from '@/constants';
import { SelectableMixin } from '@/core/mixins/SelectableMixin';
import { DraggableMixin } from '@/core/mixins/DraggableMixin';
import { RotatableMixin } from '@/core/mixins/RotatableMixin';

import Model, { ModelParams } from '../model';
import { TableSeat } from './tableSeat';
import SVG from 'svg.js';
import { POS } from '@/store/modules/pos';

export type TableModelparams = Omit<ModelParams, 'seats'> & {
  seats: TableSeat[];
  options?: { keepPosition?: boolean; keepLabel?: boolean; ts?: number };
};

export class Table extends RotatableMixin(DraggableMixin(SelectableMixin(Model))) {
  seats: TableSeat[];
  declare seatsGroup: SVG.G;
  declare label: SVG.Text;

  constructor(params: TableModelparams) {
    super(params);
    const { sid } = params;
    if (sid) {
      this.sid = sid;
    }
    this.seats = params.seats as TableSeat[];
    this.seats.forEach((s) => {
      s.table = this;
    });
    this.prepareView();
  }

  get type() {
    return TYPE.TABLE;
  }

  get subtype(): (typeof TYPE)[keyof typeof TYPE] {
    throw new Error('Method not implemented.');
  }

  get seatsCount() {
    return this.seats.length;
  }

  get x() {
    return this.attributes.position[0];
  }

  get y() {
    return this.attributes.position[1];
  }

  get seatCanBeSelectedOrDeselected() {
    if (RENDERER_MODE && this.attributes.isGrouped) {
      let flag = true;
      this.seats.forEach((s) => {
        if (
          s.attributes.status === SEATSTATUS.OCCUPIED ||
          s.attributes.status === SEATSTATUS.RESERVED ||
          s.attributes.status === SEATSTATUS.DISABLED
        ) {
          flag = false;
        } else if (!this.app.props.isShowCart && s.inCart) {
          flag = false;
        } else if (s.locked) {
          flag = false;
        }
      });
      return flag;
    } else if (!RENDERER_MODE && this.app.props.status !== EVENTSTATUS.DRAFT) {
      let flag = true;
      this.seats.forEach((s) => {
        if (
          s.attributes.status !== SEATSTATUS.FREE &&
          s.attributes.status !== SEATSTATUS.LOCKED &&
          s.attributes.status !== SEATSTATUS.DISABLED
        ) {
          flag = false;
        }
      });
      return flag;
    }
    return false;
  }

  lock() {
    this.seats.forEach((s) => s.lock());
  }

  unlock() {
    this.seats.forEach((s) => s.unlock());
  }

  prepareView() {
    this.view = this.canvas.group().addClass('table');
  }

  createLabel(x = 0, y = 0) {
    return this.canvas
      .plain(this.attributes.label)
      .font({ family: 'Roboto', style: 'normal' })
      .attr({
        x,
        y,
        'text-anchor': 'middle',
        'dominant-baseline': 'central',
      })
      .addClass('label table-label');
  }

  showTableLabel() {
    this.label.show();
  }

  hideTableLabel() {
    this.label.hide();
  }

  showLabel() {
    this.seats.forEach((s) => s.showLabel());
    this.label.show();
  }

  hideLabel() {
    this.seats.forEach((s) => s.hideLabel());
    this.label.hide();
  }

  changeLabel(label: string = SEAT.LABEL) {
    label = label.trim();
    this.attributes.label = label;
    this.label.plain(this.attributes.label);
  }

  clearLabel() {
    this.changeLabel();
  }

  changeSeatsCount(value: number) {
    if (this.seats.length === value) return;
    if (this.seats.length > value) {
      const deletableSeats = this.seats.filter((s) => {
        return (
          !s.locked &&
          s.attributes.status !== SEATSTATUS.OCCUPIED &&
          s.attributes.status !== SEATSTATUS.RESERVED &&
          !s.inCart
        );
      });

      for (let i = 0, len = this.seats.length; i < len - value; i += 1) {
        const seat = deletableSeats[i];
        if (!seat) break;
        this.seats.splice(this.seats.indexOf(seat), 1)[0].destroy();
      }

      this.seats = sortBy(this.seats, (s) => s.attributes.orderid);
      this.seats.map((s, index) => {
        s.attributes.orderid = index;
      });
    } else {
      for (let i = this.seats.length; i < value; i += 1) {
        const seat = TableSeat.create({ attributes: { orderid: this.seatsCount }, table: this });
        this.seatsGroup.add(seat.view);
        this.seats.push(seat);
      }
    }
    if (this.app.currentSection) {
      eventbus.emit('section:category:assign', this.app.currentSection, this.app.currentSection.categoryId);
    }
    this.render();
  }

  select() {
    this.seats.forEach((s) => s.select());
    this.selected = true;
  }

  deselect() {
    this.seats.forEach((s) => s.deselect());
    this.selected = false;
  }

  assignCategory(categoryId: number) {
    const seats: TableSeat[] = [];
    this.seats.forEach((s) => {
      if (s.seatCanBeSelectedOrDeselected) {
        seats.push(s);
      }
    });
    seats.forEach((s) => s.assignCategory(categoryId));
  }

  unassignCategory() {
    const seats: TableSeat[] = [];
    this.seats.forEach((s) => {
      if (s.seatCanBeSelectedOrDeselected) {
        seats.push(s);
      }
    });
    seats.forEach((s) => s.unassignCategory());
  }

  assignPos(pos: POS) {
    const seats: TableSeat[] = [];
    this.seats.forEach((s) => {
      if (s.seatCanBeSelectedOrDeselected) {
        seats.push(s);
      }
    });
    seats.forEach((s) => s.assignPos(pos));
  }

  unassignPos() {
    const seats: TableSeat[] = [];
    this.seats.forEach((s) => {
      if (s.seatCanBeSelectedOrDeselected) {
        seats.push(s);
      }
    });
    seats.forEach((s) => s.unassignPos());
  }

  destroy() {
    let canbeDestroyed = true;
    if (this.seats) {
      this.seats.forEach((s) => {
        if (!s.seatCanBeSelectedOrDeselected) {
          canbeDestroyed = false;
        }
      });
    }
    if (canbeDestroyed) {
      this.view.remove();
      eventbus.emit('unit:removed', this);
    }
  }

  changeMeta1(value: string) {
    this.setAttribute('meta1', value);
  }

  changeMeta2(value: string) {
    this.setAttribute('meta2', value);
  }

  toJSON(keepLabel?: boolean): TableModelparams {
    const { type, subtype, uuid, sid } = this;
    const { cx, cy } = this.rbox();
    const rotation = this.view.transform('rotation' as any) as unknown as number;
    const position = [cx, cy];
    const attributes = Object.assign({}, this.attributes, { position, rotation });
    attributes.label = attributes.label === SEAT.LABEL ? '' : attributes.label;
    return {
      uuid,
      sid,
      type,
      subtype,
      attributes,
      seats: invokeMap(this.seats, 'toJSON', keepLabel),
    };
  }

  updatePosition(x: number, y: number) {
    this.attributes.position = [x, y];
    this.shape.center(x, y);
    this.render();
    this.label.attr({ x, y });
  }

  render() {
    throw new Error('Method not implemented.');
  }

  onScaleChange() {
    throw new Error('Method not implemented.');
  }
}
